import React from 'react'
import { motion } from 'framer-motion'
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  FireIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

interface CrisisAlert {
  id: string
  type: 'spam_accusation' | 'negative_feedback' | 'angry_customer' | 'hostile_response' | 'reputation_threat'
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  description: string
  source: {
    subreddit: string
    post_title: string
    user: string
  }
  timestamp: string
  status: 'new' | 'acknowledged' | 'responding' | 'resolved'
  recommended_action: string
  response_strategy?: {
    approach: string
    tone: string
    key_points: string[]
  }
}

interface CrisisAlertsData {
  active_alerts: CrisisAlert[]
  recent_resolved: CrisisAlert[]
  stats: {
    total_alerts: number
    resolved_today: number
    avg_resolution_time: number
    escalation_rate: number
  }
}

interface CrisisAlertsPanelProps {
  data?: CrisisAlertsData
  onViewAllAlerts?: () => void
  onResponseTemplates?: () => void
}

const CrisisAlertsPanel: React.FC<CrisisAlertsPanelProps> = ({ data, onViewAllAlerts, onResponseTemplates }) => {
  const activeAlerts = data?.active_alerts || []
  const recentResolved = data?.recent_resolved || []
  const stats = data?.stats

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-100 border-red-200'
      case 'high':
        return 'text-orange-600 bg-orange-100 border-orange-200'
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200'
      case 'low':
        return 'text-blue-600 bg-blue-100 border-blue-200'
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <FireIcon className="w-5 h-5" />
      case 'high':
        return <ExclamationTriangleIcon className="w-5 h-5" />
      case 'medium':
        return <ExclamationCircleIcon className="w-5 h-5" />
      default:
        return <ExclamationCircleIcon className="w-5 h-5" />
    }
  }

  const getTypeDisplay = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Crisis Management</h3>
        </div>
        {activeAlerts.length > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {activeAlerts.length} Active Alert{activeAlerts.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xl font-semibold text-red-600">{stats.total_alerts}</p>
            <p className="text-sm text-red-700">Total Alerts</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xl font-semibold text-green-600">{stats.resolved_today}</p>
            <p className="text-sm text-green-700">Resolved Today</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xl font-semibold text-blue-600">{Math.round(stats.avg_resolution_time / 60)}m</p>
            <p className="text-sm text-blue-700">Avg Resolution</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-xl font-semibold text-purple-600">{((stats.escalation_rate || 0) * 100).toFixed(1)}%</p>
            <p className="text-sm text-purple-700">Escalation Rate</p>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Active Alerts</h4>
        {activeAlerts.length > 0 ? (
          <div className="space-y-3">
            {activeAlerts.map((alert, index) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border rounded-lg p-4 ${getSeverityColor(alert.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {getSeverityIcon(alert.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h5 className="text-sm font-medium text-gray-900">{alert.title}</h5>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-white bg-opacity-60">
                          {getTypeDisplay(alert.type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{alert.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-600">
                        <span>r/{alert.source.subreddit}</span>
                        <span>by u/{alert.source.user}</span>
                        <span>{new Date(alert.timestamp).toLocaleTimeString()}</span>
                      </div>
                      {alert.response_strategy && (
                        <div className="mt-3 p-2 bg-white bg-opacity-60 rounded text-xs">
                          <p className="font-medium">Recommended Strategy:</p>
                          <p>{alert.response_strategy.approach} - {alert.response_strategy.tone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="p-1 text-gray-600 hover:text-gray-900">
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-gray-600 hover:text-green-600">
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-green-50 rounded-lg">
            <CheckIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-green-700 font-medium">No active crisis alerts</p>
            <p className="text-xs text-green-600">All systems running smoothly</p>
          </div>
        )}
      </div>

      {/* Recent Resolved Alerts */}
      {recentResolved.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recently Resolved</h4>
          <div className="space-y-2">
            {recentResolved.slice(0, 3).map((alert, index) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-2 bg-green-50 rounded text-sm"
              >
                <div className="flex items-center space-x-2">
                  <CheckIcon className="w-4 h-4 text-green-600" />
                  <span className="text-gray-900">{alert.title}</span>
                  <span className="text-gray-500">in r/{alert.source.subreddit}</span>
                </div>
                <span className="text-green-600 text-xs">
                  {new Date(alert.timestamp).toLocaleDateString()}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex space-x-3">
          <button
            onClick={onViewAllAlerts}
            className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
          >
            <ExclamationTriangleIcon className="w-4 h-4" />
            <span>View All Alerts</span>
          </button>
          <button
            onClick={onResponseTemplates}
            className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span>Response Templates</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrisisAlertsPanel